import { certificatePathFor, findCertificateBySlug } from "@/data/certificateSlugs";
import { findGuideRoute, guideHref } from "@/data/guideRoutes";

export type LocalizedPaths = {
  sv: string;
  en: string;
};

/** Public localized page pairs (indexable marketing + legal + drug hub pages). */
export const STATIC_PATHS: LocalizedPaths[] = [
  { sv: "/", en: "/en" },
  { sv: "/lakarintyg", en: "/en/lakarintyg" },
  { sv: "/kontakt", en: "/en/contact" },
  { sv: "/boka-tid", en: "/en/book-appointment" },
  { sv: "/fragor-och-svar", en: "/en/faq" },
  { sv: "/infor-besoket", en: "/en/before-visit" },
  { sv: "/sa-fungerar-det", en: "/en/how-it-works" },
  { sv: "/guider", en: "/en/guides" },
  { sv: "/foretag", en: "/en/companies" },
  { sv: "/foretag/arbetsmiljo", en: "/en/companies/occupational-health" },
  { sv: "/foretag/drogtestning", en: "/en/companies/drug-testing" },
  { sv: "/foretag/tjanster", en: "/en/companies/services" },
  { sv: "/foretag/kontakt", en: "/en/companies/contact" },
  { sv: "/foretag/boka-tid", en: "/en/companies/book" },
  { sv: "/halsokontroller", en: "/en/health-checks" },
  { sv: "/om-oss", en: "/en/about-us" },
  { sv: "/integritetspolicy", en: "/en/privacy-policy" },
  { sv: "/cookie-policy", en: "/en/cookie-policy" },
  { sv: "/anvandarvillkor", en: "/en/terms-of-use" },
  { sv: "/bekraftelse", en: "/en/confirmation" },
  { sv: "/starta-arende", en: "/en/start-case" },
  { sv: "/provtagning", en: "/en/sample-collection" },
  { sv: "/drogtestning", en: "/en/drug-testing" },
  { sv: "/drogtestning/privat", en: "/en/drug-testing/private" },
  { sv: "/drogtestning/for-intyg", en: "/en/drug-testing/for-certificate" },
  { sv: "/drogtestning/forfragan", en: "/en/drug-testing/enquiry" },
];

export const NOINDEX_PATHS = new Set(["/bekraftelse", "/en/confirmation"]);

export const trimTrailingSlash = (path: string) =>
  path.length > 1 ? path.replace(/\/+$/, "") : path;

export const getLocalizedPathsForRoute = (pathname: string): LocalizedPaths | null => {
  const normalized = trimTrailingSlash(pathname);
  if (normalized === "/läkarintyg") {
    return { sv: "/lakarintyg", en: "/en/lakarintyg" };
  }

  for (const item of STATIC_PATHS) {
    if (trimTrailingSlash(item.sv) === normalized || trimTrailingSlash(item.en) === normalized) {
      return item;
    }
  }

  const svCertMatch = normalized.match(/^\/lakarintyg\/([^/]+)$/);
  if (svCertMatch) {
    const internal = findCertificateBySlug(svCertMatch[1], "sv");
    if (internal) {
      return { sv: certificatePathFor(internal, "sv"), en: certificatePathFor(internal, "en") };
    }
  }

  const enCertMatch = normalized.match(/^\/en\/medical-certificates\/([^/]+)$/);
  if (enCertMatch) {
    const internal = findCertificateBySlug(enCertMatch[1], "en");
    if (internal) {
      return { sv: certificatePathFor(internal, "sv"), en: certificatePathFor(internal, "en") };
    }
  }

  const svGuideMatch = normalized.match(/^\/guider\/([^/]+)\/([^/]+)$/);
  if (svGuideMatch) {
    const route = findGuideRoute(svGuideMatch[1], svGuideMatch[2]);
    if (route) {
      return {
        sv: guideHref(route.category, route.slug, "sv"),
        en: guideHref(route.category, route.slug, "en"),
      };
    }
  }

  const enGuideMatch = normalized.match(/^\/en\/guides\/([^/]+)\/([^/]+)$/);
  if (enGuideMatch) {
    const route = findGuideRoute(enGuideMatch[1], enGuideMatch[2]);
    if (route) {
      return {
        sv: guideHref(route.category, route.slug, "sv"),
        en: guideHref(route.category, route.slug, "en"),
      };
    }
  }

  const svDrugBook = normalized.match(/^\/drogtestning\/boka\/([^/]+)$/);
  if (svDrugBook && (svDrugBook[1] === "salivtest" || svDrugBook[1] === "urinprov")) {
    const enSlug = svDrugBook[1] === "salivtest" ? "saliva" : "urine";
    return {
      sv: `/drogtestning/boka/${svDrugBook[1]}`,
      en: `/en/drug-testing/book/${enSlug}`,
    };
  }

  const enDrugBook = normalized.match(/^\/en\/drug-testing\/book\/([^/]+)$/);
  if (enDrugBook && (enDrugBook[1] === "saliva" || enDrugBook[1] === "urine")) {
    const svSlug = enDrugBook[1] === "saliva" ? "salivtest" : "urinprov";
    return {
      sv: `/drogtestning/boka/${svSlug}`,
      en: `/en/drug-testing/book/${enDrugBook[1]}`,
    };
  }

  return null;
};

export const getCanonicalUrl = (pathname: string, lang: "sv" | "en", baseUrl: string) => {
  const localized = getLocalizedPathsForRoute(pathname);
  if (!localized) {
    return `${baseUrl}${pathname === "/" ? "/" : trimTrailingSlash(pathname)}`;
  }
  const canonicalPath = lang === "sv" ? localized.sv : localized.en;
  return `${baseUrl}${canonicalPath}`;
};

/** Public pages: index,follow. Confirmation + unknown/404: noindex,follow. */
export const getRobotsContent = (pathname: string) => {
  const normalized = trimTrailingSlash(pathname);
  if (NOINDEX_PATHS.has(normalized)) return "noindex, follow";
  if (!getLocalizedPathsForRoute(pathname)) return "noindex, follow";
  return "index, follow";
};
